import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { TwMainLayout } from "../../../components/tailwind";
import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import { setSectionLoading } from "../../../redux/reducers/loadingReducer";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings, getUrlForClasses } from "../../../utils/confighelpers";
import { STRIPE_SETUP_ROUTE } from "../../../utils/onboarding";
import { PAYMENTS_REQUIRED_TEXT, PaymentsRequiredDialog, usePaymentsGate } from "../../../components/PaymentsRequired";
import { FcConfirmDialog, Icon } from "../schedule/ui";
import ImportMembersDrawer from "./ImportMembersDrawer";
import InviteCodeModal from "./InviteCodeModal";
import MemberFormDrawer from "./MemberFormDrawer";
import TrialLinkModal from "./TrialLinkModal";
import MakeMemberModal from "./MakeMemberModal";
import { apiResult, Avatar, Badge, EnvelopeIcon, fmtDate, memberCredits, memberStatus, phoneOf, resendWelcomeEmail } from "./memberUtils";
import "../../../styles/onboarding.css";

/**
 * My Members (new design). Route /mymembers.
 * List from getMyClubMembers (50 per page, server-side search); status filter is front-end only
 * (Active / Guest / Not activated / Removed) on the members loaded so far. Guests booked a free trial class
 * (/trial); "Add to members" turns them into members.
 */

const PAGE_SIZE = 50;
const DOTS = <><circle cx="12" cy="5" r="1.6" fill="currentColor" /><circle cx="12" cy="12" r="1.6" fill="currentColor" /><circle cx="12" cy="19" r="1.6" fill="currentColor" /></>;
export const ICONS = {
  addUser: <><circle cx="9" cy="8" r="4" /><path d="M2 21a7 7 0 0 1 14 0M19 8v6M16 11h6" /></>,
  send: <><path d="m22 2-7 20-4-9-9-4z" /><path d="M22 2 11 13" /></>,
  download: <><path d="M12 3v12M7 10l5 5 5-5" /><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /></>,
  card: <><rect x="3" y="4" width="18" height="16" rx="3" /><circle cx="9" cy="11" r="2.5" /><path d="M5.5 17a3.5 3.5 0 0 1 7 0M15 10h3M15 14h3" /></>,
  edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>,
  removeUser: <><circle cx="9" cy="8" r="4" /><path d="M2 21a7 7 0 0 1 14 0M16 11h6" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  mail: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></>,
  link: <><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" /><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" /></>,
  users: <><circle cx="9" cy="8" r="4" /><path d="M2 21a7 7 0 0 1 14 0" /><path d="M16 4a4 4 0 0 1 0 8M22 21a7 7 0 0 0-4-6.3" /></>,
};

const FILTERS = [
  ["all", "All"],
  ["active", "Active"],
  ["guest", "Guests"],
  ["pending", "Not activated"],
  ["removed", "Removed"],
];

export default function MembersPage({ title = "My Members" }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { addSuccessToast, addErrorToast } = useToast();
  const userReduxState = useSelector(userState);
  const businessId = userReduxState.userData?._id;

  const [members, setMembers] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [filter, setFilter] = React.useState("all");
  const [form, setForm] = React.useState({ open: false, member: null });
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [importOpen, setImportOpen] = React.useState(false);
  const [trialOpen, setTrialOpen] = React.useState(false);
  const [makeMember, setMakeMember] = React.useState(null); // consumer_detail of a guest
  const [removing, setRemoving] = React.useState(null);
  const [removeBusy, setRemoveBusy] = React.useState(false);
  const reqRef = React.useRef(0);
  // Adding, inviting, importing and the trial link wait until the club's payment account is set up.
  const payments = usePaymentsGate();
  const stripeReady = payments.ready;
  const whenStripeReady = payments.whenReady;
  const openAdd = () => whenStripeReady(() => setForm({ open: true, member: null }));
  const openInvite = () => whenStripeReady(() => setInviteOpen(true));
  const openImport = () => whenStripeReady(() => setImportOpen(true));
  const openTrial = () => whenStripeReady(() => setTrialOpen(true));

  // The Setup guide links here with state { open: "add" | "invite" | "import" } to open that flow directly.
  const location = useLocation();
  React.useEffect(() => {
    const want = location.state?.open;
    if (!want) return;
    if (want === "add") openAdd();
    else if (want === "invite") openInvite();
    else if (want === "import") openImport();
    else if (want === "trial") openTrial();
    navigate(location.pathname, { replace: true, state: { ...location.state, open: undefined } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.open]);

  React.useEffect(() => {
    dispatch(setSectionLoading(false)); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // debounce the search box; search from 2 characters (or cleared)
  React.useEffect(() => {
    const q = searchInput.trim();
    const t = setTimeout(() => setSearch(q.length >= 2 ? q : ""), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = React.useCallback(
    async (pageNo = 1) => {
      if (!businessId) return;
      const req = ++reqRef.current;
      if (pageNo === 1) setLoading(true);
      else setLoadingMore(true);
      try {
        const res = await axiosInstance({
          method: "get",
          url: `${getUrlForClasses("")}getMyClubMembers?invite=used&page=${pageNo}&limit=${PAGE_SIZE}&businessId=${businessId}${search ? `&searchText=${encodeURIComponent(search)}` : ""}`,
        });
        if (req !== reqRef.current) return;
        const [ok, data] = apiResult(res);
        const rows = ok && Array.isArray(data?.data) ? data.data : [];
        setTotal(ok ? parseInt(data?.total, 10) || rows.length : 0);
        setMembers((prev) => (pageNo === 1 ? rows : [...prev, ...rows.filter((r) => !prev.some((p) => p._id === r._id))]));
        setPage(pageNo);
      } catch (e) {
        if (req === reqRef.current && pageNo === 1) setMembers([]);
      } finally {
        if (req === reqRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [businessId, search]
  );

  React.useEffect(() => {
    load(1);
  }, [load]);

  const reload = () => load(1);

  const counts = FILTERS.reduce((acc, [k]) => ({ ...acc, [k]: k === "all" ? members.length : members.filter((m) => memberStatus(m).key === k).length }), {});
  const visible = members.filter((m) => filter === "all" || memberStatus(m).key === filter);
  const hasMore = members.length < total;
  const openMember = (m) => navigate(`/mymembers/detail?id=${m?.consumer_detail?.consumerId}&mid=${m._id}`);

  const removeMember = async () => {
    if (!removing) return;
    setRemoveBusy(true);
    try {
      const res = await axiosInstance({ method: "post", url: `${getUrlForBookings()}removeMember`, data: { consumerId: removing.consumer_detail?.consumerId } });
      const [ok, , msg] = apiResult(res);
      if (ok) {
        addSuccessToast(`${removing.consumer_detail?.fullName || "Member"} removed`);
        setRemoving(null);
        reload();
      } else addErrorToast(msg || "Couldn't remove this member.");
    } catch (e) {
      addErrorToast("Couldn't remove this member.");
    } finally {
      setRemoveBusy(false);
    }
  };

  const noMembersAtAll = !loading && !search && members.length === 0;

  return (
    <TwMainLayout
      children={
        <div className="flex-1 px-4 pb-16 pt-6 sm:px-8 lg:px-10">
          {/* Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-black tracking-tight text-gray-900 sm:text-3xl">{title}</h1>
              <p className="mt-1 max-w-xl text-sm text-gray-600 sm:text-base">Everyone who joined your club. Open a member to see their passes, bookings and payments.</p>
            </div>
            <AddMembersMenu onAdd={openAdd} onInvite={openInvite} onTrial={openTrial} onImport={openImport} />
          </div>

          {!stripeReady && (
            <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-[#F5D9A8] bg-[#FFF7E8] px-4 py-3.5 sm:flex-row sm:items-center sm:px-5" role="status">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FDE7C2] text-[#8A5300]" aria-hidden="true">
                <Icon className="h-5 w-5" d={<><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></>} />
              </span>
              <p className="flex-1 text-sm text-[#5C3A00]">
                <span className="font-extrabold">Set up payments to start adding members.</span>{" "}
                Members buy their passes and subscriptions through your club's payment account, so it needs to be ready before they join.
              </p>
              <button type="button" onClick={() => navigate(STRIPE_SETUP_ROUTE)} className="shrink-0 rounded-xl bg-brown-coffee px-4 py-2.5 text-sm font-extrabold text-white hover:bg-light-brown-coffee">
                Set up payments
              </button>
            </div>
          )}

          {noMembersAtAll ? (
            <div className="mt-8 flex flex-col items-center rounded-3xl border border-dashed border-[#E3D0F7] bg-white px-6 py-14 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#F3E9FF] text-indigo-bz">
                <Icon className="h-8 w-8" d={ICONS.users} />
              </span>
              <h2 className="mt-5 text-xl font-black text-gray-900">No members yet</h2>
              <p className="mt-2 max-w-md text-sm text-gray-600 sm:text-base">Add members yourself, invite them with a code, or bring your list over from another system.</p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                <button type="button" onClick={openInvite} className="rounded-2xl border border-gray-200 bg-white px-5 py-3 text-sm font-extrabold text-gray-900 hover:bg-gray-50">
                  Invite with a code
                </button>
                <button type="button" onClick={openAdd} className="rounded-2xl bg-brown-coffee px-5 py-3 text-sm font-extrabold text-white hover:bg-light-brown-coffee">
                  Add a member
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center">
                <div className="relative w-full lg:w-96">
                  <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-gray-400">
                    <Icon className="h-5 w-5" d={ICONS.search} />
                  </span>
                  <input
                    type="search"
                    aria-label="Search members"
                    placeholder="Search name, email or phone"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="block h-12 w-full rounded-2xl border border-gray-200 bg-white pl-11 pr-4 text-base text-gray-900 outline-none placeholder:text-gray-400 focus:border-indigo-bz focus:ring-4 focus:ring-[#F3E9FF]"
                  />
                </div>
                <div role="tablist" aria-label="Filter members" className="flex gap-1 overflow-x-auto rounded-2xl bg-[#F3EEF5] p-1">
                  {FILTERS.map(([k, l]) => (
                    <button
                      key={k}
                      type="button"
                      role="tab"
                      aria-selected={filter === k}
                      onClick={() => setFilter(k)}
                      className={classNames("h-9 shrink-0 whitespace-nowrap rounded-xl px-3.5 text-sm font-extrabold", filter === k ? "bg-white text-brown-coffee shadow-sm" : "text-gray-500 hover:text-gray-900")}
                    >
                      {l} <span className="opacity-60">{loading ? "" : counts[k]}</span>
                    </button>
                  ))}
                </div>
                <span className="text-sm font-bold text-gray-600 lg:ml-auto">{loading ? "" : `${total} ${total === 1 ? "member" : "members"}`}</span>
              </div>

              {/* List */}
              <div className="mt-5 rounded-3xl border border-gray-200 bg-white shadow-[0_10px_30px_rgba(72,27,58,0.05)]">
                <div className="hidden grid-cols-[minmax(0,2.3fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.9fr)_52px] gap-3 px-5 pb-2.5 pt-4 md:grid">
                  {["Member", "Member since", "Last active", "Credits", "Status", ""].map((h, i) => (
                    <span key={i} className="text-xs font-extrabold uppercase tracking-wide text-gray-500">{h}</span>
                  ))}
                </div>
                {loading ? (
                  <div aria-hidden="true">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="flex animate-pulse items-center gap-3 border-t border-gray-100 px-5 py-4 first:border-t-0 md:first:border-t">
                        <span className="h-11 w-11 shrink-0 rounded-full bg-[#EDE5F0]" />
                        <span className="flex-1">
                          <span className="block h-4 w-40 rounded-full bg-[#EDE5F0]" />
                          <span className="mt-2 block h-3 w-56 max-w-full rounded-full bg-[#F3EEF5]" />
                        </span>
                        <span className="hidden h-4 w-24 rounded-full bg-[#F3EEF5] md:block" />
                        <span className="hidden h-4 w-24 rounded-full bg-[#F3EEF5] md:block" />
                        <span className="h-5 w-20 rounded-full bg-[#F3EEF5]" />
                      </div>
                    ))}
                  </div>
                ) : visible.length === 0 ? (
                  <div className="border-t border-gray-100 px-6 py-12 text-center text-sm text-gray-600 md:border-t">
                    {search ? `No members match “${search}”.` : "No members in this list."}
                  </div>
                ) : (
                  <ul>
                    {visible.map((m) => (
                      <MemberRow
                        key={m._id}
                        member={m}
                        onOpen={() => openMember(m)}
                        onResend={async () => {
                          const [ok, msg] = await resendWelcomeEmail(m.consumer_detail?.consumerId);
                          ok ? addSuccessToast(msg) : addErrorToast(msg);
                        }}
                        onEdit={() => setForm({ open: true, member: m })}
                        onRemove={() => setRemoving(m)}
                        onMakeMember={() => setMakeMember(m.consumer_detail)}
                      />
                    ))}
                  </ul>
                )}
                {!loading && hasMore && (
                  <div className="flex justify-center border-t border-gray-100 p-3">
                    <button type="button" disabled={loadingMore} onClick={() => load(page + 1)} className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF] disabled:opacity-60">
                      {loadingMore && <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#E3D0F7] border-t-indigo-bz" />}
                      Show more members
                    </button>
                  </div>
                )}
              </div>
              {filter !== "all" && hasMore && !loading && (
                <p className="mt-2 text-xs text-gray-500">The filter applies to the {members.length} members loaded so far. Load more to see the rest.</p>
              )}
            </>
          )}

          <MemberFormDrawer
            open={form.open}
            member={form.member}
            onClose={() => setForm({ open: false, member: null })}
            onSaved={reload}
          />
          <InviteCodeModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
          <TrialLinkModal open={trialOpen} onClose={() => setTrialOpen(false)} />
          <MakeMemberModal open={!!makeMember} guest={makeMember} onClose={() => setMakeMember(null)} onDone={reload} />
          <ImportMembersDrawer open={importOpen} onClose={() => setImportOpen(false)} onImported={reload} />
          <PaymentsRequiredDialog {...payments.dialog} message={PAYMENTS_REQUIRED_TEXT.members} />
          <FcConfirmDialog
            open={!!removing}
            danger
            busy={removeBusy}
            title={`Remove ${removing?.consumer_detail?.fullName || "this member"}?`}
            message="They lose access to your club, including any credits and subscriptions they still have. This can't be undone."
            confirmLabel="Remove member"
            cancelLabel="Keep member"
            onClose={() => setRemoving(null)}
            onConfirm={removeMember}
          />
        </div>
      }
    />
  );
}

export function AddMembersMenu({ onAdd, onInvite, onTrial, onImport }) {
  const items = [
    { label: "Add a member", sub: "Enter their details yourself", icon: ICONS.addUser, tone: "bg-[#F3E9FF] text-indigo-bz", onClick: onAdd },
    { label: "Invite with a code", sub: "They join from the FitClub app", icon: ICONS.send, tone: "bg-[#FFE8EB] text-[#D3263A]", onClick: onInvite },
    ...(onTrial ? [{ label: "Trial class link", sub: "1 free class for newcomers", icon: ICONS.link, tone: "bg-[#FFF1DC] text-[#B45F00]", onClick: onTrial }] : []),
    { label: "Import members", sub: "Move a list over from another system, with credits", icon: ICONS.download, tone: "bg-[#E8F7EE] text-[#1F6B3A]", onClick: onImport },
  ];
  return (
    <Menu as="div" className="relative">
      <MenuButton className="flex w-full items-center justify-center gap-2 rounded-2xl bg-brown-coffee px-5 py-3 text-base font-extrabold text-white shadow-[0_10px_24px_rgba(72,27,58,0.25)] hover:bg-light-brown-coffee sm:w-auto">
        <Icon className="h-5 w-5" d={<path d="M12 5v14M5 12h14" />} />
        Add members
        <Icon className="h-4 w-4" d={<path d="m6 9 6 6 6-6" />} />
      </MenuButton>
      <MenuItems anchor="bottom end" className="z-[60] mt-2 w-80 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl focus:outline-none">
        {items.map((it) => (
          <MenuItem key={it.label}>
            <button type="button" onClick={it.onClick} className="flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left data-[focus]:bg-[#F7F4F8]">
              <span className={classNames("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", it.tone)}>
                <Icon className="h-[18px] w-[18px]" d={it.icon} />
              </span>
              <span>
                <span className="block text-sm font-extrabold text-gray-900">{it.label}</span>
                <span className="mt-0.5 block text-xs font-semibold text-gray-500">{it.sub}</span>
              </span>
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}

function MemberRow({ member, onOpen, onEdit, onRemove, onResend, onMakeMember }) {
  const c = member.consumer_detail || {};
  const status = memberStatus(member);
  const credits = memberCredits(member);
  const removed = status.key === "removed";
  const contact = [c.email, phoneOf(c)].filter(Boolean).join(" · ");
  const guest = status.key === "guest";
  const lastActive = status.key === "pending" ? "Never" : fmtDate(c.lastActivity) || "–";
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-t border-gray-100 px-4 py-3 first:border-t-0 hover:bg-[#FBF8FE] sm:px-5 md:grid-cols-[minmax(0,2.3fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.9fr)_52px] md:first:border-t">
      <button type="button" onClick={onOpen} className="flex min-w-0 items-center gap-3 text-left">
        <Avatar src={c.profilePic} name={c.fullName} muted={removed} />
        <span className="min-w-0">
          <span className={classNames("block truncate text-[15px] font-extrabold", removed ? "text-gray-400" : "text-gray-900")}>{c.fullName || "Member"}</span>
          <span className="block truncate text-[13px] text-gray-500">{contact}</span>
          <span className="mt-1 flex flex-wrap gap-1.5 md:hidden">
            <Badge tone={credits.tone}>{credits.label}</Badge>
            {status.key !== "active" && <Badge tone={status.tone}>{status.label}</Badge>}
          </span>
        </span>
      </button>
      <span className="hidden text-sm text-gray-700 md:block">{fmtDate(c.memberSince || member.createdAt) || "–"}</span>
      <span className="hidden text-sm text-gray-700 md:block">{lastActive}</span>
      <span className="hidden md:block"><Badge tone={credits.tone}>{credits.label}</Badge></span>
      <span className="hidden md:block"><Badge tone={status.tone}>{status.label}</Badge></span>
      <Menu as="div" className="relative flex justify-end">
        <MenuButton aria-label={`Options for ${c.fullName || "member"}`} className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 data-[open]:bg-gray-100">
          <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">{DOTS}</svg>
        </MenuButton>
        <MenuItems anchor="bottom end" className="z-[60] mt-1 w-56 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl focus:outline-none">
          <MenuItem>
            <button type="button" onClick={onOpen} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-900 data-[focus]:bg-[#F7F4F8]">
              <Icon className="h-4 w-4 text-gray-500" d={ICONS.card} />
              View member
            </button>
          </MenuItem>
          {!removed && (
            <>
              <MenuItem>
                <button type="button" onClick={onEdit} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-900 data-[focus]:bg-[#F7F4F8]">
                  <Icon className="h-4 w-4 text-gray-500" d={ICONS.edit} />
                  Edit details
                </button>
              </MenuItem>
              {guest && (
                <MenuItem>
                  <button type="button" onClick={onMakeMember} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-900 data-[focus]:bg-[#F7F4F8]">
                    <Icon className="h-4 w-4 text-gray-500" d={ICONS.addUser} />
                    Add to members
                  </button>
                </MenuItem>
              )}
              {status.key === "pending" && (
                <MenuItem>
                  <button type="button" onClick={onResend} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-gray-900 data-[focus]:bg-[#F7F4F8]">
                    <EnvelopeIcon aria-hidden="true" className="size-4 text-gray-400" />
                    Resend welcome email
                  </button>
                </MenuItem>
              )}
              <MenuItem>
                <button type="button" onClick={onRemove} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-[#B4232F] data-[focus]:bg-[#FFF5F6]">
                  <Icon className="h-4 w-4" d={ICONS.removeUser} />
                  Remove member
                </button>
              </MenuItem>
            </>
          )}
        </MenuItems>
      </Menu>
    </li>
  );
}
